import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";

import { useCallback, useEffect } from "react";
import PrimaryButton from "../../../../../../common/components/primary_button";
import { ProcessoAuditoriasLista } from "../../../../domain/models/processo_auditorias_lista";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { FormRangedTimeMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_ranged_time_masked_input";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";
import {
  AuditoriaProcessoAauditoriaForm,
  AuditoriaProcessoAauditoriaSchema,
} from "../../../schemas/Auditoria_auditoria/auditoria_processo_auditoria";
import { CentroCustoDropDown } from "../../../../../../common/domain/models/centro_custo_dropdown";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { DefaultFormCheckBox } from "../../../../../../common/components/default_form_checkbox";
import { DropDownFuncionario } from "../../../../../../common/components/dropdown_funcionario";

type props = {
  open: boolean;
  saving: boolean;
  selectedProcessoAuditoriaCopia: ProcessoAuditoriasLista | undefined;
  IdAuditoria: number;
  centroCustoDropdown: CentroCustoDropDown[];
  onClose: () => void;
  onSubmit: (form: AuditoriaProcessoAauditoriaForm) => void;
};

export const CopiaProcessoAuditoriaModal = ({
  open,
  saving,
  selectedProcessoAuditoriaCopia,
  IdAuditoria,
  centroCustoDropdown,
  onClose,
  onSubmit,
}: props) => {
  const hookForm = useForm<AuditoriaProcessoAauditoriaForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(AuditoriaProcessoAauditoriaSchema),
  });
  const isResponsavelManual = hookForm.watch("ResponsavelCadastrado");

  useEffect(() => {
    if (open) {
      hookForm.reset({});
      if (selectedProcessoAuditoriaCopia) {
        hookForm.reset({
          DescResponsavel: selectedProcessoAuditoriaCopia.Responsavel,
          Descricao: selectedProcessoAuditoriaCopia.Processo,
          Horario: selectedProcessoAuditoriaCopia.Horario,
          IdAuditoria: selectedProcessoAuditoriaCopia.IdAuditoria,
          RequisitoNormativo: selectedProcessoAuditoriaCopia.RequisitoNormativo,
          FuncionarioNome: selectedProcessoAuditoriaCopia.FuncionarioNome,
          IdFuncionario: selectedProcessoAuditoriaCopia.IdFuncionario,
          IdProcesso: selectedProcessoAuditoriaCopia.IdProcesso,
          Local: selectedProcessoAuditoriaCopia.Local,
          Data: selectedProcessoAuditoriaCopia.Data
            ? new Date(selectedProcessoAuditoriaCopia.Data)
            : new Date(),

          ResponsavelCadastrado:
            String(
              selectedProcessoAuditoriaCopia.ResponsavelCadastrado,
            ).toLowerCase() === "false"
              ? true
              : false,
        });
      }
    } else {
      hookForm.reset({
        IdAuditoria: IdAuditoria,
        ResponsavelCadastrado: false,
      });
    }
  }, [hookForm, open, IdAuditoria, selectedProcessoAuditoriaCopia]);

  const handleSubmit = useCallback(
    (form: AuditoriaProcessoAauditoriaForm) => {
      if (!saving) {
        const formParaSalvar = {
          ...form,
          ResponsavelCadastrado: form.ResponsavelCadastrado,
        };

        onSubmit(formParaSalvar);
      }
    },
    [onSubmit, saving],
  );
  const onErrors = useCallback((errors: any) => {
    console.error("Erros de validação impedindo o submit:", errors);
  }, []);

  const handleClose = useCallback(() => {
    if (!saving) onClose();
  }, [saving, onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: {
          component: "form",
          sx: { width: 400 },
          onSubmit: hookForm.handleSubmit(handleSubmit, onErrors),
        },
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "45rem",
          },
        },
      }}
    >
      <DialogTitle>Copiar Processo de Auditoria?</DialogTitle>
      <DialogContent>
        <ModalLoading loading={saving}>
          <FormContainer title="Copia" arrangement="fullRow">
            <div className="flex flex-col gap-4">
              <div className="w-full">
                <DefaultFormInput
                  name="Descricao"
                  label="Processo"
                  required={true}
                  rows={4}
                  multiline={true}
                  reactHookForm={hookForm}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormDatePickerInput
                  required={true}
                  name="Data"
                  label="Data"
                  reactHookForm={hookForm}
                />
                <FormRangedTimeMaskedInput
                  name="Horario"
                  label={"Horário"}
                  reactHookForm={hookForm}
                />
              </div>
              <div className="w-full">
                <DefaultFormInput
                  name="RequisitoNormativo"
                  label="Requisito Normativo"
                  rows={2}
                  multiline={true}
                  reactHookForm={hookForm}
                />
              </div>
              <div className="w-full col-span-1 lg:col-span-2">
                <DefaultFormAutocomplete
                  name="Local"
                  label="Local"
                  options={centroCustoDropdown}
                  getOptionLabel={(x) => x.title ?? ""}
                  getOptionKey={(x) => x.id}
                  reactHookForm={hookForm}
                />
              </div>

              {/* Seção Responsável */}
              <div className="flex flex-col gap-2 p-2 border border-gray-100 rounded bg-gray-50/50">
                <div className="w-full">
                  {isResponsavelManual === true ? (
                    <DefaultFormInput
                      name="DescResponsavel"
                      label="Nome do Responsável Manual"
                      reactHookForm={hookForm}
                      required={true}
                    />
                  ) : (
                    <DropDownFuncionario
                      name="IdFuncionario"
                      readOnly={saving}
                      reactHookForm={hookForm}
                      label="Selecionar Funcionário do Sistema"
                      required={true}
                    />
                  )}
                </div>
                <div className="w-full">
                  <DefaultFormCheckBox
                    name="ResponsavelCadastrado"
                    label="Preencher o responsável manualmente?"
                    reactHookForm={hookForm}
                  />
                </div>
              </div>
            </div>
          </FormContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-end flex px-4 pb-4 gap-4">
          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}
              >
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton type="submit" buttonColor="primary-green">
                  Sim
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
};
